import {
  computed,
  DestroyRef,
  inject,
  Injectable,
  INJECTOR,
  linkedSignal,
  resource,
  signal,
} from '@angular/core';
import { CartRepository } from '../repositories/cart.repository';
import {
  AudioService,
  handleRequestError,
  Scan,
  ScannerStore,
  SOFTLINE_FEATURE_SCANNER,
} from '@softline/application';
import {
  catchError,
  concatMap,
  defer,
  filter,
  finalize,
  from,
  map,
  merge,
  Observable,
  of,
  skip,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Cart, CartItem, FinalizeResult } from '@softapps/wws/pos-core';
import { askDeleteFn, MessageBarService, modalFn } from '@softline/ui-core';
import { isDefinedNotEmpty, Store } from '@softline/core';
import { Router } from '@angular/router';
import { CashierAgeApprovalModalComponent } from '@softapps/wws/pos-core';
import { LedControlService } from './led-control.service';

type AddEvent =
  { type: 'scan'; data: Scan } | { type: 'click'; data: { productId: number } };

const NO_SCAN_PAGES = ['/payment', '/choose-bon', '/config'];

@Injectable({ providedIn: 'root' })
export class CartService {
  readonly #store = inject(Store);
  readonly #injector = inject(INJECTOR);
  readonly #router = inject(Router);
  readonly #cartRepo = inject(CartRepository);
  readonly #audioService = inject(AudioService);
  readonly #messageBarService = inject(MessageBarService);
  readonly #ledControlService = inject(LedControlService);

  readonly #askDelete = askDeleteFn();
  readonly #modal = modalFn();

  /** The only cart id the module acts on; pages adopt route ids through adoptRouteCartId(). */
  readonly cartId = signal<number | undefined>(undefined);
  readonly customerCardScanning = signal(false);
  readonly isAddingItem = signal(false);
  readonly approvedAge = signal<number>(0);
  readonly ageApprovalOpen = signal<boolean>(false);

  #creating: Promise<number> | null = null;

  readonly #cartResource = resource({
    params: () => this.cartId(),
    loader: async ({ params }) => {
      if (params === undefined) return undefined;
      try {
        const result = await this.#cartRepo.loadCart(params);
        if (result && result.items.length > 0)
          this.#selectedCartItem.set(result.items[result.items.length - 1]);
        return result;
      } catch (e) {
        handleRequestError(e, this.#injector, { show: true });
        return undefined;
      }
    },
  });

  /** A cart set from a mutation response for the same id is newer than the GET that was
      issued when the id changed, so the loaded value must not replace it. */
  readonly #cart = linkedSignal<Cart | null | undefined, Cart | null | undefined>({
    source: () => (this.#cartResource.hasValue() ? this.#cartResource.value() : null),
    computation: (loaded, previous) => {
      const current = previous?.value;
      if (loaded && current && current.id === loaded.id) return current;
      return loaded;
    },
  });
  readonly cart = computed(() => this.#cart());
  readonly cartError = computed(() => this.#cartResource.status() === 'error');
  readonly #selectedCartItem = signal<CartItem | null | undefined>(null);
  readonly selectedCartItem = computed(() => this.#selectedCartItem());

  readonly scan$ = this.#store
    .observe(SOFTLINE_FEATURE_SCANNER, ScannerStore.getters.latest)
    .pipe(skip(1));

  readonly #addItem$ = new Subject<{ productId: number }>();

  constructor() {
    const destroyRef = inject(DestroyRef);

    const addEvents$: Observable<AddEvent> = merge(
      this.scan$.pipe(
        filter((scan): scan is Scan => !!scan),
        map((scan) => ({ type: 'scan', data: scan }) as AddEvent)
      ),
      this.#addItem$.pipe(map((item) => ({ type: 'click', data: item }) as AddEvent))
    );

    addEvents$
      .pipe(
        filter(() => this.isScanAllowed()),
        concatMap((event) =>
          defer(() => {
            this.isAddingItem.set(true);
            return this.handleAddEvent(event);
          }).pipe(
            catchError((error) => {
              handleRequestError(error, this.#injector, { show: true });
              this.#audioService.play('warning');
              return of(null);
            }),
            finalize(() => this.isAddingItem.set(false))
          )
        ),
        takeUntilDestroyed(destroyRef)
      )
      .subscribe((cart) => {
        if (!cart) return;
        this.setCart(cart);
        this.setSelectedCartItem(cart.items[cart.items.length - 1]);
      });
  }

  isScanAllowed(): boolean {
    const url = this.#router.url;
    return (
      url.includes('/self-checkout') && !NO_SCAN_PAGES.some((page) => url.includes(page))
    );
  }

  private handleAddEvent(event: AddEvent): Observable<Cart | null> {
    if (event.type === 'click') {
      return this.addItem$(event.data.productId, 1).pipe(
        concatMap((cart) => (cart ? this.approveAgeRestriction$(cart) : of(cart)))
      );
    }

    const scan = event.data;
    if (this.isCustomerCard(scan)) {
      this.customerCardScanning.set(true);
      return this.scanCustomerCard$(scan).pipe(
        finalize(() => this.customerCardScanning.set(false)),
        tap({
          complete: () => {
            this.#audioService.play('approved');
            this.#messageBarService.success(
              'Ihre Kundenkarte wurde erfolgreich gescannt'
            );
          },
        })
      );
    }

    return this.scanItem$(scan, 1).pipe(
      concatMap((cart) => (cart ? this.approveAgeRestriction$(cart) : of(cart)))
    );
  }

  private isCustomerCard(scan: Scan): boolean {
    const data = scan.data.toLowerCase();
    return (
      (scan.labelType === 'code39' &&
        (data.startsWith('kk') || data.startsWith('kto'))) ||
      scan.data.startsWith('8011')
    );
  }

  approveAgeRestriction$(cart: Cart): Observable<Cart | null> {
    if (cart.items.length < 1) return of(cart);

    const hasSalesRestriction = (cartItem: CartItem) =>
      (cartItem.product?.salesRestriction?.age ?? 0) > 0;

    const item = [...cart.items].reverse().find(hasSalesRestriction);
    if (!item) return of(cart);

    const ageRestriction = item.product.salesRestriction?.age ?? 0;
    if (ageRestriction <= this.approvedAge()) return of(cart);

    this.ageApprovalOpen.set(true);
    const approval = (async (): Promise<boolean> => {
      try {
        await this.#ledControlService.signalAssistance();
      } catch (e) {
        handleRequestError(e, this.#injector, { show: false });
      }

      const result = await this.#modal({
        component: CashierAgeApprovalModalComponent,
        dismiss: false,
        title: 'Aktion erforderlich',
        data: { age: ageRestriction, cartItem: item },
      });

      if (result !== true) return false;
      this.approvedAge.set(ageRestriction);
      return true;
    })();

    return defer(() => from(approval)).pipe(
      finalize(() => this.ageApprovalOpen.set(false)),
      concatMap((approved) => {
        if (approved) return of(cart);
        this.#audioService.play('warning');
        return this.removeDeclinedItem$(cart, item);
      })
    );
  }

  /** The item is already in the backend cart when the cashier decides, so a decline must
      remove it again or the customer pays for it at the payment page. */
  private removeDeclinedItem$(cart: Cart, item: CartItem): Observable<Cart | null> {
    const cartId = cart.id ?? this.cartId();
    if (!cartId) return of(null);
    return defer(() => from(this.#cartRepo.deleteItem(item.key, cartId)));
  }

  setCart(cart: Cart | null | undefined) {
    this.#cart.set(cart);
  }

  setSelectedCartItem(item: CartItem | null | undefined) {
    this.#selectedCartItem.set(item);
  }

  /** Returns the id a page must show: the service id wins over a stale route id; a route id is
      only adopted when the service has none yet (deep link, reload). */
  adoptRouteCartId(routeCartId: number | null): number | undefined {
    const current = this.cartId();
    if (current !== undefined) return current;
    if (routeCartId === null) return undefined;
    this.cartId.set(routeCartId);
    return routeCartId;
  }

  getOrCreateCart(): Promise<number> {
    const cartId = this.cartId();
    if (cartId) return Promise.resolve(cartId);
    return this.createCart();
  }

  /** Concurrent callers share one request, so a scan during creation cannot open a second cart. */
  createCart(): Promise<number> {
    if (this.#creating) return this.#creating;
    this.#creating = (async () => {
      try {
        const createdCart = await this.#cartRepo.createCart();
        this.cartId.set(createdCart.id);
        return createdCart.id;
      } finally {
        this.#creating = null;
      }
    })();
    return this.#creating;
  }

  scanItem$(scan: Scan, quantity: number): Observable<Cart | null> {
    return defer(() => from(this.getOrCreateCart())).pipe(
      concatMap((cartId) => this.#cartRepo.addScanItem$(cartId, scan, quantity))
    );
  }

  private addItem$(productId: number, quantity: number): Observable<Cart | null> {
    return defer(() => from(this.getOrCreateCart())).pipe(
      concatMap((cartId) => this.#cartRepo.addItem$(cartId, productId, quantity))
    );
  }

  private scanCustomerCard$(scan: Scan): Observable<Cart | null> {
    return defer(() => from(this.getOrCreateCart())).pipe(
      concatMap((cartId) => {
        if (scan.data.startsWith('8011')) {
          return defer(() =>
            from(this.#cartRepo.updateCart(cartId, { customerCardNumber: scan.data }))
          );
        }

        return this.#cartRepo.scanCustomerCard$(scan).pipe(
          switchMap((result) => {
            const changes: Partial<Cart> & { customerCardNumber?: string } = {};
            const cardNumber = result?.customerCardNumber;

            if (isDefinedNotEmpty(cardNumber)) {
              const lower = cardNumber.toLowerCase();
              changes.customerCardNumber = lower.startsWith('kk_')
                ? cardNumber.slice(3)
                : lower.startsWith('kto_')
                  ? cardNumber.slice(4)
                  : cardNumber;
            } else if (isDefinedNotEmpty(result?.customerNumber)) {
              changes.accountId = result.customerNumber;
            }

            if (!isDefinedNotEmpty(changes)) return of(null);
            return defer(() => from(this.#cartRepo.updateCart(cartId, changes)));
          })
        );
      })
    );
  }

  private getCartItem(key: string): CartItem | null {
    return this.cart()?.items.find((cartItem) => key === cartItem.key) ?? null;
  }

  updateQuantity$(
    key: string,
    newQuantity: number,
    unitCompositionId?: number | null
  ): Observable<Cart | null> {
    const cartId = this.cartId();
    const cartItem = this.getCartItem(key);
    if (!cartItem || !cartId) return of(null);

    if (newQuantity === 0)
      return defer(() => from(this.deleteItem(key)).pipe(map((o) => o ?? null)));

    return this.#cartRepo.updateItem$(cartId, key, {
      key: cartItem.key,
      quantity: newQuantity,
      unitCompositionId: unitCompositionId ?? cartItem?.unitCompositionId ?? undefined,
    });
  }

  addItem(productId: number): void {
    this.#addItem$.next({ productId });
  }

  clear() {
    this.cartId.set(undefined);
    this.#cart.set(undefined);
    this.#selectedCartItem.set(null);
    this.approvedAge.set(0);
  }

  async removeCart(): Promise<void> {
    const cartId = this.cartId();
    if (!cartId) return;
    await this.#cartRepo.removeCart(cartId);
    this.clear();
  }

  /** Deletes the current cart when nothing was added to it, so leaving the module does not
      leave an open empty cart on the backend. */
  async discardEmptyCart(): Promise<void> {
    const cartId = this.cartId();
    const cart = this.cart();
    if (!cartId || !cart) return;
    if (cart.items.length > 0 || (cart.cashbookEntries?.length ?? 0) > 0) return;
    try {
      await this.#cartRepo.removeCart(cartId);
    } catch (e) {
      handleRequestError(e, this.#injector, { show: false });
    }
  }

  async deleteItem(key: string): Promise<Cart | null> {
    const cartId = this.cartId();
    const cart = this.cart();
    if (!cartId || !cart) return null;
    const result = await this.#askDelete({
      title: 'Artikel löschen',
      question: 'Wollen Sie den Artikel löschen?',
    });
    if (result === 'DISMISSED' || result === 'NO') return cart;
    return (await this.#cartRepo.deleteItem(key, cartId)) ?? null;
  }

  /** Resolves undefined when the backend rejected the finalize; the cart stays active. */
  async finalize(body: unknown): Promise<FinalizeResult | undefined> {
    const cartId = this.cartId();
    if (!cartId) return undefined;

    try {
      const result: FinalizeResult = await this.#cartRepo.finalize(cartId, body);
      this.clear();
      return result;
    } catch (e) {
      handleRequestError(e, this.#injector, { show: true });
      return undefined;
    }
  }
}
